import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from 'src/app/services/payment/payment.service';
import { PaymentRequest } from './models/PaymentRequest';

import Swal from 'sweetalert2';
import { PromotionService } from 'src/app/services/promotion/promotion.service';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.css'],
})
export class PaymentComponent {
  product: any;
  paymentForm: FormGroup;
  promotionDiscount: number = 0;
  price: number = 0;
  stateServiceId: string = '';
  stateKey: string = '';
  isPromotionApplied: boolean = false;
  isLoadingPromotion: boolean = false;
  promotionError: string = '';
  appliedPromotionCode: string = '';
  paymentCompleted: boolean = false;

  paymentRequest: PaymentRequest = {
    amount: 0,
    title: '',
    description: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    referenceType: '',
    referenceId: '',
    promotionCode: '',
  };

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private promotionService: PromotionService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.stateKey = navigation?.extras.state?.['productKey'];
    console.log(this.stateKey);
    this.stateServiceId = navigation?.extras.state?.['serviceSystemId'];
    console.log(this.stateServiceId);

    this.route.queryParams.subscribe((params) => {
      if (params['payment'] === 'success') {
        this.handlePaymentSuccess();
      } else if (!this.stateKey) {
        this.stateKey = params['productKey'];
        this.stateServiceId = params['serviceSystemId'];
      }
    });

    if (this.stateKey) {
      this.getProduct(this.stateKey);
    }

    this.paymentForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      email: ['', [Validators.required, Validators.email]],
      promotion: [''],
    });
  }

  handlePaymentSuccess() {
    this.paymentCompleted = true;
    this.price = 0;
    this.promotionDiscount = 0;
    this.paymentRequest.amount = 0;
    this.clearFormData();

    Swal.fire({
      icon: 'success',
      title: 'Payment Successful!',
      text: 'Your payment has been completed successfully',
      confirmButtonColor: '#28a745',
      confirmButtonText: 'OK',
    });
  }

  getProduct(productKey: string) {
    this.paymentService.getProduct(productKey).then((product: any) => {
      console.log('📌 [PRODUCT RESPONSE FROM BACKEND]:', product);

      if (!product) {
        Swal.fire({
          icon: 'error',
          title: 'Product not found',
          text: `No pricing entry was found for "${productKey}".`,
          confirmButtonColor: '#dc3545',
        }).then(() => {
          this.router.navigate(['/company/job-posts']);
        });
        return;
      }

      this.product = product;
      this.price = product.amount;
      this.paymentRequest.amount = product.amount;
      this.paymentRequest.description = product.description;
      this.paymentRequest.title = product.name;
    });
  }

  useCode() {
    const promoCode = this.paymentForm.get('promotion')?.value?.trim();

    if (!promoCode) {
      this.promotionError = 'Please enter a promotion code';
      Swal.fire({
        icon: 'warning',
        title: 'Warning',
        text: 'Please enter a promotion code',
        confirmButtonColor: '#0a376e',
      });
      return;
    }

    if (this.isPromotionApplied) {
      return;
    }

    this.isLoadingPromotion = true;
    this.promotionError = '';

    const userId = localStorage.getItem('userId') || undefined;

    this.promotionService
      .validatePromo(promoCode, this.stateKey, userId)
      .subscribe({
        next: (response: any) => {
          console.log('Promotion validation response:', response);

          if (response.valid) {
            this.isPromotionApplied = true;
            this.appliedPromotionCode = promoCode;

            const discountValue = response.discountValue;
            const discountType = response.discountType;

            let discountAmount = 0;

            if (discountType === 'PERCENTAGE') {
              discountAmount = (this.price * discountValue) / 100;
            } else if (discountType === 'FIXED') {
              discountAmount = discountValue;
            }

            if (discountAmount > this.price) {
              discountAmount = this.price;
            }

            this.promotionDiscount = discountAmount;
            this.paymentRequest.amount = this.price - discountAmount;

            const discountText =
              discountType === 'PERCENTAGE'
                ? `${discountValue}%`
                : `${discountValue} EGP`;

            Swal.fire({
              icon: 'success',
              title: 'Success!',
              text: `Promotion code applied successfully! Discount: ${discountText}`,
              confirmButtonColor: '#28a745',
              timer: 3000,
            });
          } else {
            this.promotionError =
              response.message || 'Promotion code is invalid';
            Swal.fire({
              icon: 'error',
              title: 'Invalid Code',
              text: this.promotionError,
              confirmButtonColor: '#dc3545',
            });
          }

          this.isLoadingPromotion = false;
        },
        error: (err) => {
          console.error('Promotion validation error:', err);
          this.promotionError =
            err.error?.message || 'Failed to validate promotion code';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: this.promotionError,
            confirmButtonColor: '#dc3545',
          });
          this.isLoadingPromotion = false;
        },
      });
  }

  removePromotion() {
    Swal.fire({
      title: 'Remove Promotion?',
      text: 'Are you sure you want to remove this promotion code?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.isPromotionApplied = false;
        this.appliedPromotionCode = '';
        this.promotionDiscount = 0;
        this.paymentRequest.amount = this.price;
        this.paymentForm.get('promotion')?.enable();
        this.paymentForm.get('promotion')?.setValue('');
        this.promotionError = '';

        Swal.fire({
          icon: 'info',
          title: 'Removed',
          text: 'Promotion code has been removed',
          confirmButtonColor: '#0a376e',
          timer: 2000,
        });
      }
    });
  }

  isFormValid(): boolean {
    const firstName = this.paymentForm.get('firstName')?.valid;
    const lastName = this.paymentForm.get('lastName')?.valid;
    const phone = this.paymentForm.get('phone')?.valid;
    const email = this.paymentForm.get('email')?.valid;

    return !!(firstName && lastName && phone && email);
  }

  clearFormData() {
    localStorage.removeItem('cvFormData');
    localStorage.removeItem('activeStep');
    localStorage.removeItem('cvImagePath');
  }

  resetAndGoToFirstStep() {
    this.paymentCompleted = false;
    this.price = 0;
    this.promotionDiscount = 0;
    this.paymentForm.reset();
    this.clearFormData();

    this.router.navigate(['/generatResumeSteps'], {
      queryParams: { step: 0 },
      state: { reset: true },
    });
  }

  onSubmit() {
    if (this.isFormValid()) {
      let form = this.paymentForm.getRawValue();

      this.paymentRequest.firstName = form.firstName;
      this.paymentRequest.lastName = form.lastName;
      this.paymentRequest.email = form.email;
      this.paymentRequest.phoneNumber = form.phone;
      this.paymentRequest.referenceType = this.stateKey;
      this.paymentRequest.referenceId = this.stateServiceId;
      this.paymentRequest.promotionCode = this.isPromotionApplied
        ? this.appliedPromotionCode
        : '';

      Swal.fire({
        title: 'Processing Payment...',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      this.paymentService.sendPaymentRequest(this.paymentRequest).subscribe({
        next: (res: any) => {
          localStorage.setItem('redirectProductKey', this.stateServiceId);

          const checkoutUrl = res?.checkoutUrl?.trim();

          Swal.close();

          if (checkoutUrl && checkoutUrl.startsWith('http')) {
            this.clearFormData();

            Swal.fire({
              icon: 'success',
              title: 'Payment Link Generated',
              text: 'Redirecting to payment gateway...',
              timer: 2000,
              showConfirmButton: false,
            }).then(() => {
              window.open(checkoutUrl, '_blank');

              this.paymentCompleted = true;
              this.price = 0;
              this.promotionDiscount = 0;
              this.paymentRequest.amount = 0;
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'Payment link is invalid or missing',
              confirmButtonColor: '#dc3545',
            }).then(() => {
              this.router.navigate(['/payment-redirect']);
            });
          }
        },
        error: (err) => {
          Swal.fire({
            icon: 'error',
            title: 'Payment Failed',
            text: 'Payment request failed. Please try again',
            confirmButtonColor: '#dc3545',
          });
        },
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Form',
        text: 'Please fill all required fields',
        confirmButtonColor: '#0a376e',
      });
    }
  }
}
